import { GENERATION_FAILURE_REASON_LABELS, type GenerationFailureReason, type GenerationKind } from "@storyline/shared";
import AssetThumbnail from "@/src/common/components/asset-thumbnail";
import Pill from "@/src/common/components/pill";
import RegenerateControl from "@/src/stories/components/regenerate-control";
import { FRAME_STATUS_LABELS, FRAME_STATUS_TONES } from "@/src/stories/constants/contact-copy";
import { type FrameSlotState, FrameStatus } from "@/src/stories/utils/frame-state";

type FrameSlotProps = {
	state: FrameSlotState;
	caption: string;
	costUsd: number;
	onRegenerate: (kind: GenerationKind, reason: GenerationFailureReason) => void;
};

const placeholderFor = (state: FrameSlotState): string => {
	if (state.status === FrameStatus.INHERITED) {
		return "Héritée du plan précédent";
	}
	if (state.status === FrameStatus.FAILED) {
		return state.failureReason ? `Échec · ${GENERATION_FAILURE_REASON_LABELS[state.failureReason]}` : "Échec";
	}
	if (state.isBusy) {
		return "Génération en cours";
	}
	return "En attente de génération";
};

const FrameSlot: React.FC<FrameSlotProps> = ({ state, caption, costUsd, onRegenerate }) => {
	const canRegenerate =
		state.status === FrameStatus.GENERATED || state.status === FrameStatus.FAILED || state.status === FrameStatus.PENDING;
	const displayUrl = state.status === FrameStatus.INHERITED ? state.inheritedFromUrl : state.imageUrl;

	return (
		<div className="flex flex-1 flex-col gap-1">
			<AssetThumbnail
				imageUrl={displayUrl}
				isLoading={state.isBusy}
				placeholder={placeholderFor(state)}
				caption={caption}
				badge={<Pill tone={FRAME_STATUS_TONES[state.status]}>{FRAME_STATUS_LABELS[state.status]}</Pill>}
			/>
			{canRegenerate ? (
				<RegenerateControl isBusy={state.isBusy} costUsd={costUsd} onRegenerate={(reason) => onRegenerate(state.generationKind, reason)} />
			) : null}
		</div>
	);
};

export default FrameSlot;
